import BEM from 'common/bem';
import { User, Group } from 'types';
import UserAvatarNoPresence from 'common/components/UserAvatar/UserAvatarNoPresence';

const classes = BEM.with('KeywordSearchSideBar');

interface ConversationMetadata {
  group_id: string;
  peer_token: string;
  is_dl_list: boolean;
}

export function isCurrentUser({
  currentUserId,
  senderToken,
  senderUserToken,
  myRoleIds,
  senderRoleToken,
}: {
  currentUserId: string;
  senderToken: string;
  senderUserToken: string;
  senderRoleToken: string;
  myRoleIds: string[];
}) {
  return (
    currentUserId === senderToken ||
    currentUserId === senderUserToken ||
    myRoleIds?.includes(senderToken) ||
    (senderRoleToken !== '' && myRoleIds?.includes(senderRoleToken))
  );
}

export function renderBody(highlightBody: string, entityBody: string) {
  const isValid = (value: string) => value && value !== 'null' && value.trim() !== '';

  const truncate = (value: string, maxLength: number) =>
    value.length > maxLength ? value.slice(0, maxLength) + '...' : value;

  const content = isValid(highlightBody)
    ? highlightBody
    : isValid(entityBody)
    ? truncate(entityBody, 60)
    : null;

  if (!content) return null;

  return <div className={classes('message')} dangerouslySetInnerHTML={{ __html: content }} />;
}

export function renderAvatar(sender: User) {
  return sender ? <UserAvatarNoPresence user={sender} size={34} /> : null;
}

export function getStoreModel(
  entityType: string,
  id: string,
  getEntityData: (entityType: string, id: string) => User | Group
) {
  try {
    const model = getEntityData(entityType, id);

    if (isGroup(model) && model.groupType === 'ROLE_P2P' && model.p2pRecipient) {
      return model.p2pRecipient as User | Group;
    }

    return model as User | Group;
  } catch (e) {
    return null;
  }
}

function isGroup(entity: User | Group): entity is Group {
  return (
    entity &&
    typeof entity === 'object' &&
    '$entityType' in entity &&
    entity.$entityType === 'group' &&
    'groupType' in entity
  );
}

export function getLoadTime(startTimeRef: number) {
  const MAX_LOAD_TIME = 30000;
  const currentTime = performance.now();
  const loadTime = startTimeRef ? currentTime - startTimeRef : 0;
  const loadTimeMs = loadTime && loadTime > MAX_LOAD_TIME ? '30,000+' : Math.round(loadTime);
  return loadTimeMs;
}

export function getConversationName(
  conversationMetadata: ConversationMetadata,
  getEntityData: (entityType: string, id: string) => User | Group
) {
  const { group_id, peer_token, is_dl_list } = conversationMetadata || {};
  const id = group_id || peer_token;

  if (!id) return 'Error: ID not found';

  const modelType = group_id ? 'group' : is_dl_list ? 'distributionList' : 'user';

  const entity = getStoreModel(modelType, id, getEntityData);

  return entity?.displayName || 'Error: Conversation name not found';
}

export function isScrollSuccessful(messageElement: Element, scrollContainer: Element): boolean {
  const messageRect = messageElement.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const isMessageVisible =
    messageRect.top >= containerRect.top && messageRect.bottom <= containerRect.bottom;

  return isMessageVisible;
}
